import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notice, PageHeader, Spinner } from "@/components/common/States";
import { api, errorMessage } from "@/lib/api";

const JENJANG = ["SD atau Sederajat", "SMP atau Sederajat", "SMA atau Sederajat", "SMK atau Sederajat"];

// Kolom persis form uji coba di landing page; kirimannya masuk menu Pengajuan Pusat Kendali
// bertanda sumber reseller ini, bukan lagi email admin@globalconnex.id.
const KOSONG = {
  school_name: "", jenjang: "", student_count: "", city: "",
  pic_name: "", pic_role: "", email: "", phone: "", message: "",
};

export default function MitraDaftar() {
  const qc = useQueryClient();
  const [f, setF] = useState(KOSONG);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sukses, setSukses] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const kirim = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/reseller/pengajuan", {
        school_name: f.school_name,
        jenjang: f.jenjang,
        student_count: f.student_count === "" ? null : Number(f.student_count),
        city: f.city,
        pic_name: f.pic_name,
        pic_role: f.pic_role || null,
        email: f.email,
        phone: f.phone,
        message: f.message || null,
      });
      await qc.invalidateQueries({ queryKey: ["notifications"] });
      setSukses(data.id);
      setF(KOSONG);
      toast.success("Pengajuan terkirim ke Pusat Kendali SIST.");
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader eyebrow="Ruang Mitra SIST" title="Pendaftaran Sekolah"
        description="Ajukan sekolah calon pelanggan Anda. Form ini sama dengan form uji coba di landing page; pengajuan masuk ke Pusat Kendali bertanda atas nama Anda, dan setelah diterima sekolah otomatis menjadi binaan Anda." />

      {sukses ? (
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-6 space-y-3" data-testid="mitra-daftar-sukses">
          <h2 className="text-lg font-bold text-emerald-900 dark:text-emerald-100">Pengajuan terkirim</h2>
          <p className="text-sm text-emerald-900 dark:text-emerald-100">
            Pusat Kendali SIST akan meninjau pengajuan sekolah ini. Anda mendapat notifikasi lonceng begitu keputusannya keluar —
            diterima (sekolah langsung menjadi binaan Anda) atau ditolak beserta alasannya.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" onClick={() => setSukses(null)} data-testid="mitra-daftar-again">
              Ajukan sekolah lain
            </Button>
            <Button asChild data-testid="mitra-daftar-to-pelanggan">
              <Link to="/mitra/pelanggan">Lihat Kelola Pelanggan <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={kirim} className="rounded-2xl border bg-card p-6 space-y-4" data-testid="mitra-daftar-form">
          <div className="space-y-2">
            <Label htmlFor="md-nama">Nama Sekolah <span className="text-[#E11D2E]">*</span></Label>
            <Input id="md-nama" required minLength={3} maxLength={150} value={f.school_name} onChange={set("school_name")}
              placeholder="Contoh: SMP Negeri 1 Jakarta" data-testid="mitra-daftar-nama" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="md-jenjang">Jenjang <span className="text-[#E11D2E]">*</span></Label>
              <select id="md-jenjang" required value={f.jenjang} onChange={set("jenjang")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="mitra-daftar-jenjang">
                <option value="">Pilih Jenjang</option>
                {JENJANG.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="md-siswa">Jumlah Siswa</Label>
              <Input id="md-siswa" type="number" min={1} max={10000} value={f.student_count} onChange={set("student_count")}
                placeholder="Contoh: 500" data-testid="mitra-daftar-siswa" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="md-kota">Kota / Kabupaten <span className="text-[#E11D2E]">*</span></Label>
            <Input id="md-kota" required minLength={2} maxLength={100} value={f.city} onChange={set("city")}
              placeholder="Contoh: Malang" data-testid="mitra-daftar-kota" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="md-pic">Nama Kontak <span className="text-[#E11D2E]">*</span></Label>
              <Input id="md-pic" required minLength={2} maxLength={100} value={f.pic_name} onChange={set("pic_name")}
                placeholder="Nama lengkap Anda" data-testid="mitra-daftar-pic" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="md-jabatan">Jabatan</Label>
              <Input id="md-jabatan" maxLength={60} value={f.pic_role} onChange={set("pic_role")}
                placeholder="Contoh: Kepala Sekolah, Admin, Guru" data-testid="mitra-daftar-jabatan" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="md-email">Email <span className="text-[#E11D2E]">*</span></Label>
              <Input id="md-email" type="email" required maxLength={200} value={f.email} onChange={set("email")}
                placeholder="email@sekolah.id" data-testid="mitra-daftar-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="md-wa">No. WhatsApp <span className="text-[#E11D2E]">*</span></Label>
              <Input id="md-wa" type="tel" required value={f.phone} onChange={set("phone")}
                placeholder="08xxxxxxxxxx" data-testid="mitra-daftar-wa" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="md-pesan">Pesan / Catatan</Label>
            <textarea id="md-pesan" maxLength={1000} value={f.message} onChange={set("message")} rows={3}
              placeholder="Ada pertanyaan atau kebutuhan khusus?"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="mitra-daftar-pesan" />
          </div>
          {err && <Notice tone="danger" testId="mitra-daftar-error">{err}</Notice>}
          <Button type="submit" disabled={busy} data-testid="mitra-daftar-submit">
            {busy ? <Spinner /> : <Send className="h-4 w-4 mr-1.5" />} Kirim Pendaftaran
          </Button>
        </form>
      )}
    </div>
  );
}
